import { log } from '../../util/log';
import { BookingContext } from '../../util/booking-context';
import { callWp } from '../../util/call-wp';
import { hideLoadingOverlay, showLoadingOverlay } from '../../util/loading-overlay';
import { loadFindTimeslotPage } from './find-timeslot';
import { loadFitzpatrickTestPage } from './fitzpatrick-test';
import { getSkinType } from '../../util/get-skin-type';
import { loadSelectLocationPage } from './select-location';
import { bindSummary, updateSummary } from '../../util/summary';
import { track } from '../../util/tracking';
import { getInjectablesCategoryId } from '../constants/constants';

export async function loadChooseServicesPage(context: BookingContext) {
	window.scroll(0, 0);
	log('Loading choose services page');
	showLoadingOverlay(context);
	context.root.innerHTML = '';
	const ssrStartTime = performance.now();
	const ssrResponse = await callWp('GET', 'view_choose_services', context.data);
	const ssrEndTime = performance.now();
	log('SSR took', ssrEndTime - ssrStartTime, 'ms');
	const html = ssrResponse.html;

	function removeURLParameters() {
		// if there are no parameters, return
		if (window.location.search.length <= 0) {
			return;
		}

		// Get the current URL without parameters
		let urlWithoutParams = window.location.origin + window.location.pathname;

		// If there is a 'book' parameter, change the url to 'my-account/treatments' instead of 'booking-online'
		if (window.location.search.includes('book')) {
			urlWithoutParams = `${silkNexusWp.myAccountUrl}treatments/`;
		}

		// Redirect to the URL without parameters without reloading the page
		window.history.replaceState({}, document.title, urlWithoutParams);
	}

	// Call the function to remove parameters
	removeURLParameters();

	// Clear the local storage when we load this page

	if (ssrResponse.error) {
		context.root.innerHTML = ssrResponse.error;
		hideLoadingOverlay(context);
		return;
	}

	if (
		ssrResponse.serviceIds &&
		ssrResponse.addonIds &&
		ssrResponse.serviceData &&
		ssrResponse.addonData &&
		ssrResponse.locationId &&
		ssrResponse.locationName
	) {
		context.data.serviceIds = ssrResponse.serviceIds;
		context.data.addonIds = ssrResponse.addonIds;
		context.data.serviceData = new Map(Object.entries(ssrResponse.serviceData));
		context.data.addonData = new Map(Object.entries(ssrResponse.addonData));
		context.data.categoryId = ssrResponse.categoryId;
		context.data.categoryName = ssrResponse.categoryName;
		context.data.locationId = ssrResponse.locationId;
		context.data.locationName = ssrResponse.locationName;
	}

	// if there is a consult in the service data, and it is not first time, remove it
	// if (context.data.serviceData && context.data.serviceData.size > 0 && context.data.firstTime === false) {
	// 	const consult = Array.from(context.data.serviceData.values()).find((s) => s.name === 'SKIN - Consult' || s.name === 'LHR - Consult');
	// 	if (consult) {
	// 		context.data.serviceData.delete(consult.id);
	// 		context.data.serviceIds = context.data.serviceIds.filter((s) => s !== consult.id);
	// 	}
	// }

	if (ssrResponse.serviceData && ssrResponse.serviceData.length > 0) {
		context.data.serviceData = new Map(Object.entries(ssrResponse.serviceData));
	}
	if (ssrResponse.addonData && ssrResponse.addonData.length > 0) {
		context.data.addonData = new Map(Object.entries(ssrResponse.addonData));
	}

	if (context.data.reschedule) {
		return proceed();
	}
	if (context.data.rebook || context.data.book) {
		context.data.rebook = false;
		return proceed();
	}

	const localStorageKey = 'silk-nexus-wp-booking-data';
	localStorage.removeItem(localStorageKey);

	if (context.data.goToTimeslots) {
		context.data.goToTimeslots = false;
		proceed();
		return;
	}

	// if (context.data.continueBooking) {
	// 	context.data.continueBooking = false;
	// 	return proceed();
	// }

	context.root.innerHTML = html;

	bindSummary(context);

	context.root.querySelector('.booking-summary__details-date')?.remove();
	context.root.querySelector('.booking-summary__details-time')?.remove();
	context.root.querySelector('.booking-summary__change-treatments')?.remove();
	context.root.querySelector('.booking-summary__next')?.remove();

	hideLoadingOverlay(context);

	context.root.querySelector('[data-step="0"]')?.addEventListener('click', () => loadSelectLocationPage(context));

	context.root.querySelectorAll<HTMLButtonElement>('button[data-reload]').forEach((b) => b.addEventListener('click', () => loadChooseServicesPage(context)));

	const nextButton = context.root.querySelector<HTMLButtonElement>('[data-services-next]');
	if (!nextButton) {
		log('next button not found');
		return;
	}

	// data-change-treatments
	const changeTreatmentsButton = context.root.querySelector<HTMLButtonElement>('button[data-change-treatments]');
	if (changeTreatmentsButton) {
		changeTreatmentsButton.addEventListener('click', () => {
			context.data.rebook = false;
			void loadChooseServicesPage(context);
		});
	}

	const categoryButtons = context.root.querySelectorAll<HTMLButtonElement>('button[data-select-category]');
	const subcategorySections = context.root.querySelectorAll<HTMLElement>('[data-subcategory-section]');
	const subcategorySelects = context.root.querySelectorAll<HTMLSelectElement>('select[data-select-subcategory]');
	const selectServicesDivs = context.root.querySelectorAll<HTMLDivElement>('[data-select-services-section]');

	let selectedCategory = '';
	let selectedSubcategory = '';

	// summary

	const updateNextButton = () => {
		if (context.data.reschedule) {
			nextButton.disabled = false;
			return;
		}

		const noServicesSelected = context.data.serviceIds.length === 0;
		// const noFirstTimeAnswer = context.data.firstTime === null;
		// const firstTimeMissingConsultRepsonse = !!(context.data.firstTime && context.data.includeConsult === null);
		const firstTimeMissingConsultRespsonse = context.data.includeConsult === null;

		const firstTimeLabelHelper = context.root.querySelector<HTMLElement>(
			`[data-select-services-section-test][data-category="${selectedCategory}"] [data-question-first] [data-helper]`
		);

		// eslint-disable-next-line @wordpress/no-unused-vars-before-return
		const firstTimeButtonYes = context.root.querySelector<HTMLButtonElement>(
			`[data-select-services-section-test][data-category="${selectedCategory}"] [data-question-first] button[data-yes]`
		);
		// eslint-disable-next-line @wordpress/no-unused-vars-before-return
		const firstTimeButtonNo = context.root.querySelector<HTMLButtonElement>(
			`[data-select-services-section-test][data-category="${selectedCategory}"] [data-question-first] button[data-no]`
		);
		firstTimeButtonYes?.classList.toggle('border-red', firstTimeMissingConsultRespsonse);
		firstTimeButtonNo?.classList.toggle('border-red', firstTimeMissingConsultRespsonse);
		firstTimeLabelHelper?.classList.toggle('d-none', !firstTimeMissingConsultRespsonse);

		// nextButton.disabled = noServicesSelected || noFirstTimeAnswer || firstTimeMissingConsultRepsonse;
		nextButton.disabled = noServicesSelected || firstTimeMissingConsultRespsonse;

		updateSummary(context, rerenderRows);
	};

	const onCategoryChange = (categoryButton: HTMLButtonElement) => {
		selectedCategory = categoryButton.dataset.id ?? context.data.categoryId ?? '';
		// eslint-disable-next-line @wordpress/no-unused-vars-before-return
		const firstTimeButtonYes = context.root.querySelector<HTMLButtonElement>(
			`[data-select-services-section-test][data-category="${selectedCategory}"] [data-question-first] button[data-yes]`
		);
		// eslint-disable-next-line @wordpress/no-unused-vars-before-return
		const firstTimeButtonNo = context.root.querySelector<HTMLButtonElement>(
			`[data-select-services-section-test][data-category="${selectedCategory}"] [data-question-first] button[data-no]`
		);

		const disableMultiModality = true;

		function clearTheSelection() {
			if (disableMultiModality) {
				context.data.serviceIds = [];
				context.data.serviceData = new Map();
				context.data.addonIds = [];
				context.data.addonData = new Map();

				// rerenderRows();
			}

			context.data.includeConsult = false;
			firstTimeButtonYes?.classList.toggle('prebook-question__button--active', false);
			firstTimeButtonYes?.classList.toggle('prebook-question__button--inactive', false);
			firstTimeButtonNo?.classList.toggle('prebook-question__button--active', false);
			firstTimeButtonNo?.classList.toggle('prebook-question__button--inactive', false);

			context.root.querySelectorAll<HTMLElement>(`[data-select-services-section-test]`).forEach((s) => s.classList.toggle('hidden', true));
			context.root
				.querySelectorAll<HTMLElement>(`[data-select-services-section-test][data-category="${selectedCategory}"]`)
				.forEach((s) => s.classList.toggle('hidden', false));

			subcategorySections.forEach((s) => s.classList.toggle('hidden', true));
			const subcategorySection = document.querySelectorAll<HTMLElement>(`[data-subcategory-section][data-category="${selectedCategory}"]`);
			subcategorySection.forEach((s) => s.classList.toggle('hidden', false));

			const subcategorySelect = document.querySelector<HTMLSelectElement>(`select[data-select-subcategory][data-category="${selectedCategory}"]`);

			if (!subcategorySelect) {
				log(`no subcategory select found for id ${selectedCategory}`);
				return;
			}
			// Default to the [data-default] option of the select
			const defaultOption = subcategorySelect.querySelector<HTMLOptionElement>('[data-default]');
			if (defaultOption) {
				log('default option found');
				subcategorySelect.querySelectorAll('option').forEach((o) => o.removeAttribute('selected'));
				defaultOption.setAttribute('selected', '');
				subcategorySelect.value = defaultOption.value;
				subcategorySelect.selectedIndex = defaultOption.index;
			}

			// set the class to active for the selected category
			categoryButtons.forEach((b) => b.classList.toggle('category-select__button--active', b.dataset.id === selectedCategory));

			// eslint-disable-next-line @wordpress/no-unused-vars-before-return

			if (firstTimeButtonYes && firstTimeButtonNo) {
				log('first time buttons found');
				context.data.includeConsult = null;
				const handleFirstTimeButtonYesClick = () => {
					firstTimeButtonYes.classList.toggle('prebook-question__button--active', true);
					firstTimeButtonYes.classList.toggle('prebook-question__button--inactive', false);
					firstTimeButtonNo.classList.toggle('prebook-question__button--active', false);
					firstTimeButtonNo.classList.toggle('prebook-question__button--inactive', true);

					context.data.includeConsult = true;

					updateNextButton();
				};

				const handleFirstTimeButtonNoClick = () => {
					firstTimeButtonYes.classList.toggle('prebook-question__button--active', false);
					firstTimeButtonYes.classList.toggle('prebook-question__button--inactive', true);
					firstTimeButtonNo.classList.toggle('prebook-question__button--active', true);
					firstTimeButtonNo.classList.toggle('prebook-question__button--inactive', false);

					context.data.includeConsult = false;

					updateNextButton();
				};

				firstTimeButtonYes.addEventListener('click', handleFirstTimeButtonYesClick);
				firstTimeButtonNo.addEventListener('click', handleFirstTimeButtonNoClick);

				if (context.data.includeConsult !== null) {
					if (context.data.includeConsult) {
						handleFirstTimeButtonYesClick();
					} else {
						handleFirstTimeButtonNoClick();
					}
				}
			} else {
				log('no first time buttons found');
				context.data.includeConsult = false;
			}

			if (selectedCategory === context.data.categoryId) {
				log('selected category is the same as the one in the context');
				onSubcategoryChange(subcategorySelect);
				return;
			}

			context.data.categoryId = categoryButton.dataset.id ?? '';
			context.data.categoryName = categoryButton.dataset.name ?? '';

			subcategorySelect.classList.toggle('hidden', false);
			// subcategorySelect.querySelectorAll<HTMLOptionElement>('option').forEach((o) => (o.selected = o.dataset.default === 'true'));

			document.querySelectorAll('[data-display-category]').forEach((e) => {
				e.textContent = context.data.categoryName;
			});

			onSubcategoryChange(subcategorySelect);
			updateNextButton();
		}

		if (disableMultiModality) {
			if ((context.data.serviceIds.length > 0 || context.data.addonIds.length > 0) && context.data.categoryId !== selectedCategory) {
				if (!document.getElementById('changeTreatmentConfirmModal')) {
					const modalHTML = `
                    <div class="modal fade" id="changeTreatmentConfirmModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
                    <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content" style="padding: 2rem; max-width: 28rem">
                            <div class="modal-body mb-3 p-0 d-flex flex-column align-items-center justify-content-center text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" height="2em" viewBox="0 0 512 512" style="margin-bottom: 1rem"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/></svg>
                            <span style="font-size: 1.5rem; font-weight: 500; margin-bottom: 1rem">Changing category will clear your current selection</span>
                            <span>Are you sure you want to continue?</span>
                            </div>
                            <div class="d-flex flex-row gap-2">
                            <button type="button" class="w-100 btn btn-primary text-white" style="font-weight: 700; font-size: 0.875rem">YES</button>
                            <button type="button" class="w-100 btn btn-black text-white" style="font-weight: 700; font-size: 0.875rem" data-bs-dismiss="modal">NO</button>
                            </div>
                        </div>
                        </div>
                    </div>
                    `;
					document.body.insertAdjacentHTML('beforeend', modalHTML);
				}

				const modalElement = document.getElementById('changeTreatmentConfirmModal')!;

				const modal = new window.bootstrap.Modal(modalElement, {
					backdrop: 'static',
					keyboard: false,
				});

				// on dismiss, show location modal
				// data-bs-dismiss
				modalElement.querySelector<HTMLButtonElement>('.btn-black')!.addEventListener('click', () => {
					modal.hide();
				});

				// on next, select clinic
				modalElement.querySelector<HTMLButtonElement>('.btn-primary')!.addEventListener('click', () => {
					clearTheSelection();
					modal.hide();
				});

				modal.show();
				return;
			}
		}
		clearTheSelection();
	};

	const onSubcategoryChange = (subcategorySelect: HTMLSelectElement) => {
		selectedSubcategory = subcategorySelect.value;

		// Get the selected option
		const selectedOption = subcategorySelect.options[subcategorySelect.selectedIndex];

		context.data.subcategoryId = selectedOption?.dataset.id ?? '';
		context.data.subcategoryName = selectedOption?.dataset.name ?? '';

		selectServicesDivs.forEach((s) => s.classList.toggle('hidden', true));

		const currentSelectServicesSections = document.querySelectorAll<HTMLElement>(
			`[data-select-services-section][data-category="${selectedCategory}"][data-subcategory="${selectedSubcategory}"]`
		);

		currentSelectServicesSections.forEach((s) => s.classList.toggle('hidden', false));
	};

	categoryButtons.forEach((categoryButton) => categoryButton.addEventListener('click', () => onCategoryChange(categoryButton)));
	subcategorySelects.forEach((subcategorySelect) => subcategorySelect.addEventListener('change', () => onSubcategoryChange(subcategorySelect)));

	// If there is selected category, select it
	if (context.data.categoryId) {
		const categoryButton = context.root.querySelector<HTMLButtonElement>(`button[data-select-category][data-id="${context.data.categoryId}"]`);
		if (categoryButton) {
			onCategoryChange(categoryButton);
		}

		// const subcategorySelect = context.root.querySelector<HTMLSelectElement>(`select[data-select-subcategory][data-category="${context.data.categoryId}"]`);
		// if (subcategorySelect) {
		// 	onSubcategoryChange(subcategorySelect);
		// }
	} else if (categoryButtons.length > 0) {
		onCategoryChange(categoryButtons[0]);
	}

	// on load if categoryParam and subcategoryParam are present, select the category and subcategory
	if (context.data.categoryParam) {
		const categoryButton = context.root.querySelector<HTMLButtonElement>(`button[data-select-category][data-name="${context.data.categoryParam}"]`);
		
		
		if (categoryButton) {
			onCategoryChange(categoryButton);
		}

		if (context.data.subcategoryParam) {
			
			const selectedCategoryContext = categoryButton?.dataset.id ?? context.data.categoryId ?? '';
			
			const subCategory = context.data.subcategoryParam;
			
			const subcategorySelect = document.querySelector<HTMLSelectElement>(`select[data-select-subcategory][data-category="${selectedCategoryContext}"]`);
			
			if (!subcategorySelect) {
				log(`no subcategory select found for id ${selectedCategoryContext}`);
				return;
			}
			
			subcategorySelect?.querySelectorAll('option').forEach((option) => {
				if (option.text === subCategory) {
					option.selected = true;
				}
			});
			
			onSubcategoryChange(subcategorySelect);
		}
		
	}

	const serviceAndAddonRows = context.root.querySelectorAll<HTMLElement>('label[data-addon], label[data-service]');

	const handleToggleServiceOrAddon = (row: HTMLElement) => {
		const checkbox = row.querySelector<HTMLInputElement>('input[type="checkbox"]');

		const isAddon = row.dataset.addon !== undefined;

		if (!checkbox) {
			return;
		}

		const isChecked = checkbox.checked;

		// If this is inject, nuke all the selections
		if (context.data.categoryId === getInjectablesCategoryId()) {
			context.data.serviceIds = [];
			context.data.serviceData = new Map();
			context.data.addonIds = [];
			context.data.addonData = new Map();
		}

		if (isAddon) {
			if (isChecked) {
				context.data.addonIds.push(row.dataset.id ?? '-');
				context.data.addonData.set(row.dataset.id ?? '-', {
					id: row.dataset.id ?? '',
					name: row.dataset.name ?? '',
					category: row.dataset.category ?? '',
					subcategory: row.dataset.subcategory ?? '',
					duration: parseInt(row.dataset.duration ?? '', 10),
				});
			} else {
				context.data.addonIds = context.data.addonIds.filter((a) => a !== row.dataset.id);
				context.data.addonData.delete(row.dataset.id ?? '-');
			}
		} else {
			// eslint-disable-next-line no-lonely-if
			if (isChecked) {
				context.data.serviceIds.push(row.dataset.id ?? '-');
				context.data.serviceData.set(row.dataset.id ?? '-', {
					id: row.dataset.id ?? '',
					name: row.dataset.name ?? '',
					category: row.dataset.category ?? '',
					subcategory: row.dataset.subcategory ?? '',
					duration: parseInt(row.dataset.duration ?? '', 10),
				});
			} else {
				context.data.serviceIds = context.data.serviceIds.filter((s) => s !== row.dataset.id);
				context.data.serviceData.delete(row.dataset.id ?? '-');
			}
		}

		// re-render all the rows

		rerenderRows();
	};

	const rerenderRows = () => {
		serviceAndAddonRows.forEach((row) => {
			const checkbox = row.querySelector<HTMLInputElement>('input[type="checkbox"]');
			const isAddon = row.dataset.addon !== undefined;

			if (!checkbox) {
				return;
			}

			const checked = isAddon ? context.data.addonIds.includes(row.dataset.id ?? '-') : context.data.serviceIds.includes(row.dataset.id ?? '-');

			checkbox.checked = checked;
		});

		// Hide the addons section if there are no service selected
		const addonsSections = context.root.querySelectorAll<HTMLElement>('.service-select__addons, .service-select_addons-label');
		addonsSections.forEach((s) => s.classList.toggle('hidden', context.data.serviceIds.length === 0));

		updateNextButton();
	};

	rerenderRows();

	serviceAndAddonRows.forEach((row) => row.addEventListener('click', () => handleToggleServiceOrAddon(row)));

	nextButton.addEventListener('click', async () => {
		if (context.data.categoryId === getInjectablesCategoryId() && !context.data.reschedule && !context.data.injectQuestionCompleted) {
			showSafetyModal();
			return;
		}

		proceed();
	});

	async function proceed() {
		const subServices = Array.from(context.data.serviceData.values()).map((s) => s.subcategory);
		const uniqueSubService = [...new Set(subServices)];

		const treatments = Array.from(context.data.serviceData.values()).map((s) => s.name);
		const uniqueTreatments = [...new Set(treatments)];

		const service = Array.from(context.data.serviceData.values()).map((s) => s.category);
		const uniqueService = [...new Set(service)];

		track({
			event: 'chooseTreatment',
			service: uniqueService[0] ?? null,
			subService: uniqueSubService,
			treatments: uniqueTreatments,
			firstTime: context.data.firstTime,
		});

		showLoadingOverlay(context);

		const userIsActivated = await callWp('GET', 'is_user_ready');
		if (!userIsActivated?.success === true) {
			if (!document.getElementById('unexpectedErrorModal')) {
				const modalHTML = `
                    <div class="modal fade" id="unexpectedErrorModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
                        <div class="modal-dialog modal-dialog-centered">
                        <div class="modal-content" style="padding: 2rem; max-width: 28rem">
                            <div class="modal-body mb-3 p-0 d-flex flex-column align-items-center justify-content-center text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" height="2em" viewBox="0 0 512 512" style="margin-bottom: 1rem"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/></svg>
                            <span>An unexpected error occurred. Please try again later.</span>
                            </div>
                            <div class="d-flex flex-row gap-2">
                            <button type="button" class="w-100 btn btn-primary text-white" style="font-weight: 700; font-size: 0.875rem">CONTINUE</button>
                            </div>
                        </div>
                        </div>
                    </div>
                    `;
				document.body.insertAdjacentHTML('beforeend', modalHTML);
			}

			const modalElement = document.getElementById('unexpectedErrorModal')!;

			const modal = new window.bootstrap.Modal(modalElement, {
				backdrop: 'static',
				keyboard: false,
			});

			// on next, select clinic
			modalElement.querySelector<HTMLButtonElement>('.btn-primary')!.addEventListener('click', () => {
				modal.hide();
				window.location.href = `${silkNexusWp.myAccountUrl}/appointments`;
			});

			modal.show();
			return;
		}

		// signedIn
		if (userIsActivated.signedIn !== true || userIsActivated.verified !== true) {
			// Save to local storage
			const data = {
				serviceIds: context.data.serviceIds,
				addonIds: context.data.addonIds,
				includeConsult: context.data.includeConsult,
				locationId: context.data.locationId,
			};
			localStorage.setItem(localStorageKey, JSON.stringify(data));
		}
		if (userIsActivated.signedIn !== true) {
			// store the state in the url
			const parameters = {
				// redirect_to_booking: true,
				serviceIds: context.data.serviceIds.join(','),
				addonIds: context.data.addonIds.join(','),
				includeConsult: context.data.includeConsult,
				goToTimeslots: true,
				locationId: context.data.locationId,
			};

			const parametersString =
				'?' +
				Object.keys(parameters)
					.map((key) => key + '=' + parameters[key])
					.join('&');
			window.location.href = `${silkNexusWp.myAccountUrl}${parametersString}`;
			return;
		}

		// User must be verified
		if (userIsActivated.verified !== true) {
			track({
				event: 'linkAccount',
				message: 'User account is not verified',
			});

			if (!document.getElementById('notVerifiedModal')) {
				const modalHTML = `
                    <div class="modal fade" id="notVerifiedModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
                        <div class="modal-dialog modal-dialog-centered">
                        <div class="modal-content" style="padding: 2rem; max-width: 28rem">
                            <div class="modal-body mb-3 p-0 d-flex flex-column align-items-center justify-content-center text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" height="2em" viewBox="0 0 512 512" style="margin-bottom: 1rem"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/></svg>
                            <span>Please finish activating your account before booking online</span>
                            </div>
                            <div class="d-flex flex-row gap-2">
                            <button type="button" class="w-100 btn btn-primary text-white" style="font-weight: 700; font-size: 0.875rem">CONTINUE</button>
                            </div>
                        </div>
                        </div>
                    </div>
                    `;
				document.body.insertAdjacentHTML('beforeend', modalHTML);
			}

			const modalElement = document.getElementById('notVerifiedModal')!;

			const modal = new window.bootstrap.Modal(modalElement, {
				backdrop: 'static',
				keyboard: false,
			});

			// on next, select clinic
			modalElement.querySelector<HTMLButtonElement>('.btn-primary')!.addEventListener('click', () => {
				modal.hide();
				window.location.href = `${silkNexusWp.myAccountUrl}/edit-account?require_verification=1`;
			});

			modal.show();
			return;
		}

		// And their account must be linked in nexus
		if (userIsActivated.linked !== true) {
			track({
				event: 'linkAccount',
				message: 'User is not linked yet',
			});

			if (!document.getElementById('accountNotSetupModal')) {
				const modalHTML = `
                    <div class="modal fade" id="accountNotSetupModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
                        <div class="modal-dialog modal-dialog-centered">
                        <div class="modal-content" style="padding: 2rem; max-width: 28rem">
                            <div class="modal-body mb-3 p-0 d-flex flex-column align-items-center justify-content-center text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" height="2em" viewBox="0 0 512 512" style="margin-bottom: 1rem"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/></svg>
                            <span>We are still setting up your online account. Please try again later. Alternatively, contact a clinic to make your booking</span>
                            </div>
                            <div class="d-flex flex-row gap-2">
                            <button type="button" class="w-100 btn btn-primary text-white" style="font-weight: 700; font-size: 0.875rem">CONTINUE</button>
                            </div>
                        </div>
                        </div>
                    </div>
                    `;
				document.body.insertAdjacentHTML('beforeend', modalHTML);
			}

			const modalElement = document.getElementById('accountNotSetupModal')!;

			const modal = new window.bootstrap.Modal(modalElement, {
				backdrop: 'static',
				keyboard: false,
			});

			// on next, select clinic
			modalElement.querySelector<HTMLButtonElement>('.btn-primary')!.addEventListener('click', () => {
				modal.hide();
				window.location.href = `${silkNexusWp.myAccountUrl}/../../booking/`;
			});

			modal.show();
			return;
		}

		continueToTimeslots();
	}

	async function continueToTimeslots() {
		context.data.therapistId = null;

		// Only do fitz if we are booking for lhr

		// const isLaser = context.data.categoryName === 'Laser Hair Removal';

		let isLaser = false;
		for (const s of context.data.serviceData.values()) {
			if (s.category === 'Laser Hair Removal') {
				isLaser = true;
			}
		}

		if (isLaser) {
			context.data.skinType = await getSkinType();

			const testFitz = false;
			if (!context.data.skinType || testFitz) {
				return loadFitzpatrickTestPage(context);
			}
		}

		// if (context.data.categoryId === getInjectablesCategoryId()) {
		// 	return loadTherapistSelection(context);
		// }

		return loadFindTimeslotPage(context);
	}

	updateNextButton();

	document.querySelectorAll('.safety-modal__overlay').forEach((overlay) => overlay.remove());
	// Add a new overlay
	const overlay = document.createElement('div');
	overlay.classList.add('safety-modal__overlay');
	overlay.addEventListener('click', closeSafetyModal);
	document.body.appendChild(overlay);

	const safetyModal = context.root.querySelector('.safety-modal__modal');

	function showSafetyModal() {
		if (!safetyModal) {
			log('no safety modal');
			return;
		}

		safetyModal.classList.toggle('safety-modal__modal--active', true);
		overlay.classList.toggle('safety-modal__overlay--active', true);
	}

	function closeSafetyModal() {
		if (!safetyModal) {
			return;
		}

		safetyModal.classList.toggle('safety-modal__modal--active', false);
		overlay.classList.toggle('safety-modal__overlay--active', false);
	}

	safetyModal?.querySelector('.safety-modal__modal-close')?.addEventListener('click', closeSafetyModal);
	safetyModal?.querySelector('[data-next]')?.addEventListener('click', () => {
		context.data.injectQuestionCompleted = true;
		closeSafetyModal();
		overlay.remove();
		proceed();
	});

	track({
		event: 'pageview',
		page_location: '/booking-online/choose-treatment',
		page_title: 'Booking online - 2 - Choose Treatment',
	});
}
